import JourneyChangeCheckoutSkeleton from '@/components/journey-change/checkout/JourneyChangeCheckoutSkeleton';
import { useJourneyChangeCheckout } from '@/hooks/useJourneyChangeCheckout';
import { useStartedExchangeOperation } from '@/hooks/useStartedExchangeOperation';
import { STORAGE_KEYS, useStorage } from '@/hooks/useStorage';
import Checkout from '@/routes/purchase/Checkout';
import type { SearchJourneyOffersDTO } from '@/types/dto/agent';
import {
  journeyChangePath,
  parseJourneyChangeUrlParams,
} from '@/utils/journeyChangeUrlParams';
import { useCallback, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

// Checkout step of the journey-change deep-link flow. It reuses the purchase
// `Checkout` for the payment UI, but `useJourneyChangeCheckout` must first commit
// the token-scoped exchange to populate the state that `Checkout` reads.
const JourneyChangeCheckout = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const selectedJourney = (
    location.state as { journey?: SearchJourneyOffersDTO } | null
  )?.journey;

  const { isLoading } = useJourneyChangeCheckout(selectedJourney);

  // No trip in hand (a direct visit or refresh drops router state) and no
  // exchange to resume — back to the selection step, keeping the deep-link
  // params. Redirect-based payments (3DS, iDEAL) return here as a fresh document
  // load, so we fall back to the persisted exchange the way
  // `/purchase/checkout` resumes the manage-booking change flow.
  const startedExchange = useStartedExchangeOperation();
  const shouldBounce = !selectedJourney && !startedExchange;
  const goToJourneySelect = useCallback(
    () => navigate(journeyChangePath(location.search), { replace: true }),
    [location.search, navigate]
  );
  useEffect(() => {
    if (shouldBounce) goToJourneySelect();
  }, [shouldBounce, goToJourneySelect]);

  // Record which fulfillment was paid for so the flow's layout guard can retire
  // every step on a back-navigation out of `/success`, and no second exchange
  // can be committed for the trip just paid for (see `routes/journey-change`).
  // `replace` keeps this paid checkout out of history as a back target.
  const { setValue: setPaidFulfillment } = useStorage<string>(
    sessionStorage,
    STORAGE_KEYS.JOURNEY_CHANGE_PAID_FULFILLMENT
  );
  const handleCompleted = useCallback(() => {
    const { fulfillment } = parseJourneyChangeUrlParams(location.search);
    if (fulfillment) setPaidFulfillment(fulfillment);
    navigate('/success', { replace: true });
  }, [location.search, navigate, setPaidFulfillment]);

  if (shouldBounce) return null;

  if (isLoading) {
    return <JourneyChangeCheckoutSkeleton />;
  }

  // Exiting the reused checkout returns to this flow's trip-selection step, not
  // the manage-booking view it never came from.
  return (
    <Checkout
      onExchangeExit={goToJourneySelect}
      onExchangeSuccess={handleCompleted}
    />
  );
};

export default JourneyChangeCheckout;
